import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import * as Sentry from "@sentry/react";
import { toast } from "@/hooks/use-toast";
import { useQuery, useMutation, useAction } from "convex/react";
import { useStoreQuery, queries as storeQueries } from "@/lib/store";
import { api } from "../../../convex/_generated/api";
import { reportWriteFailure } from "@/lib/reportWriteFailure";
import type { Id } from "../../../convex/_generated/dataModel";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import BarcodeScanner, { type ScannedFood } from "@/components/mnf/BarcodeScanner";

type Mode = "macros" | "portions" | "plan";

// Fallback targets only, used until her macro plan loads or if she has none.
// These were the ONLY targets Garden ever showed: it never read macroPlan, so
// every sister saw 1800 kcal / 150p / 200c / 60f regardless of what /macros
// calculated for her. Real targets now come from the plan; see macroTargets().
const macroGoals = [
  { key: "protein", label: "Protein", goal: 150, color: "#C0784A", icon: "egg_alt" },
  { key: "carbs",   label: "Carbs",   goal: 200, color: "#A07820", icon: "grain" },
  { key: "fat",     label: "Fat",     goal: 60,  color: "#7F77DD", icon: "water_drop" },
];

type LogEntry = {
  meal: string;
  emoji: string;
  name: string;
  serving: string;
  kcal: number;
  protein: number;
  time: string;
};

const INITIAL_LOG: LogEntry[] = [
  { meal: "morning", emoji: "🌅", name: "Steel-cut oats with berries", serving: "1 bowl (240g)", kcal: 320, protein: 12, time: "7:15a" },
  { meal: "morning", emoji: "🌅", name: "Greek yogurt + honey",        serving: "¾ cup (170g)",   kcal: 180, protein: 18, time: "7:18a" },
  { meal: "midday",  emoji: "🥗", name: "Harvest grain bowl",          serving: "1 bowl (380g)", kcal: 540, protein: 32, time: "12:40p" },
  { meal: "snack",   emoji: "🌿", name: "Apple slices + almond butter", serving: "1 medium",      kcal: 200, protein: 6,  time: "3:10p" },
];

const planDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

type SlotStatus = "eaten" | "partial" | "skipped" | "upcoming" | "planned";
type PlanSlot = {
  slot: string;
  time: string;
  food: string;
  plannedKcal: number;
  eatenKcal: number;
  family: boolean;
  status: SlotStatus;
};
type DayPlan = {
  theme: string;
  slots: PlanSlot[];
  shortfall: { protein: number; carbs: number; fat: number; fiber: number };
  note: string;
};

const KCAL_GOAL = 1800;
const MACRO_GOAL = { protein: 150, carbs: 200, fat: 60, fiber: 30 };

/** Her real targets when a plan exists, otherwise the placeholder defaults. */
function macroTargets(plan: { kcal: number; proteinG: number; carbsG: number; fatG: number } | null | undefined) {
  return {
    kcal: plan?.kcal ?? KCAL_GOAL,
    protein: plan?.proteinG ?? MACRO_GOAL.protein,
    carbs: plan?.carbsG ?? MACRO_GOAL.carbs,
    fat: plan?.fatG ?? MACRO_GOAL.fat,
  };
}

/** Index of today within the Monday-first week getWeekDates() returns. */
function todayIndex(): number {
  const dow = new Date().getDay(); // 0 = Sunday
  return dow === 0 ? 6 : dow - 1;
}

function getWeekDates(): Date[] {
  const today = new Date();
  const dow = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

const planByDay: DayPlan[] = [
  {
    theme: "Sheet-pan harvest",
    slots: [
      { slot: "Morning", time: "7:00 am", food: "Sourdough toast + eggs", plannedKcal: 380, eatenKcal: 380, family: true, status: "eaten" },
      { slot: "Midday", time: "12:30 pm", food: "Lemon chicken grain bowl", plannedKcal: 520, eatenKcal: 520, family: false, status: "eaten" },
      { slot: "Snack", time: "3:30 pm", food: "Greek yogurt + walnuts", plannedKcal: 180, eatenKcal: 140, family: false, status: "partial" },
      { slot: "Evening", time: "6:30 pm", food: "Sheet-pan salmon + roots", plannedKcal: 610, eatenKcal: 610, family: true, status: "eaten" },
    ],
    shortfall: { protein: 6, carbs: 0, fat: 0, fiber: 4 },
    note: "You nourished well, beloved.",
  },
  {
    theme: "Slow-cooker rhythm",
    slots: [
      { slot: "Morning", time: "7:00 am", food: "Steel-cut oats + berries", plannedKcal: 380, eatenKcal: 500, family: true, status: "eaten" },
      { slot: "Midday", time: "12:30 pm", food: "Harvest grain bowl", plannedKcal: 540, eatenKcal: 540, family: false, status: "eaten" },
      { slot: "Snack", time: "3:30 pm", food: "Apple + almond butter", plannedKcal: 200, eatenKcal: 200, family: false, status: "eaten" },
      { slot: "Evening", time: "6:30 pm", food: "Sweet potato shepherd's pie", plannedKcal: 680, eatenKcal: 0, family: true, status: "upcoming" },
    ],
    shortfall: { protein: 58, carbs: 62, fat: 19, fiber: 12 },
    note: "Evening meal still ahead, sister.",
  },
  {
    theme: "Garden greens",
    slots: [
      { slot: "Morning", time: "7:00 am", food: "Veggie scramble + toast", plannedKcal: 420, eatenKcal: 0, family: true, status: "planned" },
      { slot: "Midday", time: "12:30 pm", food: "Chickpea Caesar wrap", plannedKcal: 540, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Snack", time: "3:30 pm", food: "Cottage cheese + peach", plannedKcal: 200, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Evening", time: "6:30 pm", food: "Turkey meatballs + zoodles", plannedKcal: 600, eatenKcal: 0, family: true, status: "planned" },
    ],
    shortfall: { protein: 0, carbs: 0, fat: 0, fiber: 0 },
    note: "Plan looks balanced for the day ahead.",
  },
  {
    theme: "Pantry refresh",
    slots: [
      { slot: "Morning", time: "7:00 am", food: "Berry protein smoothie", plannedKcal: 360, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Midday", time: "12:30 pm", food: "Tuna salad lettuce cups", plannedKcal: 480, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Snack", time: "3:30 pm", food: "Trail mix + tea", plannedKcal: 180, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Evening", time: "6:30 pm", food: "Lentil bolognese", plannedKcal: 620, eatenKcal: 0, family: true, status: "planned" },
    ],
    shortfall: { protein: 0, carbs: 0, fat: 0, fiber: 0 },
    note: "Hearty fiber day, well planned.",
  },
  {
    theme: "Family pizza night",
    slots: [
      { slot: "Morning", time: "7:00 am", food: "Cottage cheese pancakes", plannedKcal: 400, eatenKcal: 0, family: true, status: "planned" },
      { slot: "Midday", time: "12:30 pm", food: "Mediterranean salmon bowl", plannedKcal: 520, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Snack", time: "3:30 pm", food: "Hummus + veggie sticks", plannedKcal: 180, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Evening", time: "6:30 pm", food: "Whole-wheat veggie pizza", plannedKcal: 700, eatenKcal: 0, family: true, status: "planned" },
    ],
    shortfall: { protein: 0, carbs: 0, fat: 0, fiber: 0 },
    note: "Plan a small protein boost at lunch.",
  },
  {
    theme: "Sabbath table",
    slots: [
      { slot: "Morning", time: "8:00 am", food: "Slow-baked oatmeal", plannedKcal: 420, eatenKcal: 0, family: true, status: "planned" },
      { slot: "Midday", time: "1:00 pm", food: "Roast chicken + greens", plannedKcal: 580, eatenKcal: 0, family: true, status: "planned" },
      { slot: "Snack", time: "3:30 pm", food: "Cheese + grapes", plannedKcal: 220, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Evening", time: "6:30 pm", food: "Family pasta primavera", plannedKcal: 620, eatenKcal: 0, family: true, status: "planned" },
    ],
    shortfall: { protein: 0, carbs: 0, fat: 0, fiber: 0 },
    note: "Restful and abundant.",
  },
  {
    theme: "Slow morning",
    slots: [
      { slot: "Morning", time: "8:30 am", food: "Veggie frittata + toast", plannedKcal: 440, eatenKcal: 0, family: true, status: "planned" },
      { slot: "Midday", time: "1:00 pm", food: "Quinoa harvest salad", plannedKcal: 500, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Snack", time: "3:30 pm", food: "Dark chocolate + almonds", plannedKcal: 180, eatenKcal: 0, family: false, status: "planned" },
      { slot: "Evening", time: "6:30 pm", food: "Herb-roasted salmon", plannedKcal: 600, eatenKcal: 0, family: true, status: "planned" },
    ],
    shortfall: { protein: 0, carbs: 0, fat: 0, fiber: 0 },
    note: "A gentle close to the week.",
  },
];

function statusVisuals(status: SlotStatus) {
  switch (status) {
    case "eaten":
      return { bg: "rgba(30,74,53,0.10)", color: "#1E4A35", icon: "check_circle", label: "Eaten" };
    case "partial":
      return { bg: "rgba(192,120,74,0.14)", color: "#C0784A", icon: "adjust", label: "Partial" };
    case "skipped":
      return { bg: "rgba(160,120,32,0.14)", color: "#A07820", icon: "cancel", label: "Skipped" };
    case "upcoming":
      return { bg: "rgba(127,119,221,0.14)", color: "#7F77DD", icon: "schedule", label: "Upcoming" };
    default:
      return { bg: "rgba(30,74,53,0.06)", color: "#1E4A35", icon: "circle", label: "Planned" };
  }
}

export default function Garden() {
  const [mode, setMode] = useState<Mode>("macros");
  // Was useState(1). getWeekDates() is Monday-first, so index 1 is always
  // TUESDAY — the "· Today" badge, the day's copy and the shortfall panel were
  // pinned to Tuesday on every other day of the week. A tester opening this on
  // a Sunday sees the wrong day highlighted, which reads as the app not knowing
  // what day it is.
  const [day, setDay] = useState(todayIndex());

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow={`Today · ${new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}`}
        title="Your Garden"
        subtitle={
          <>
            "I am the vine; you are the branches. If you remain in me and I in you,
            you will bear much fruit." <span style={{ color: "rgba(229,185,151,0.95)" }}>· John 15:5</span>
          </>
        }
        trailing={<Avatar initials="K" size={36} ringColor="rgba(192,120,74,0.5)" />}
      >
        {/* Mode toggle */}
        <div
          className="flex items-center p-1 mnf-body"
          style={{
            background: "rgba(245,240,232,0.10)",
            border: "1px solid rgba(245,240,232,0.18)",
            borderRadius: 999,
            backdropFilter: "blur(8px)",
          }}
        >
          {([
            { k: "macros", l: "Macros" },
            { k: "portions", l: "Hand portions" },
            { k: "plan", l: "Meal plan" },
          ] as { k: Mode; l: string }[]).map((t) => {
            const active = mode === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setMode(t.k)}
                data-testid={`tab-${t.k}`}
                className="flex-1 transition-all"
                style={{
                  height: 36,
                  borderRadius: 999,
                  background: active ? "#F5F0E8" : "transparent",
                  color: active ? "var(--mnf-f)" : "rgba(245,240,232,0.85)",
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {t.l}
              </button>
            );
          })}
        </div>
      </ScreenHeader>

      <div className="px-5 pt-5 space-y-4">
        {mode === "macros" && <MacrosView />}
        {mode === "portions" && <PortionsView />}
        {mode === "plan" && (
          <PlanView day={day} setDay={setDay} />
        )}
      </div>
    </>
  );
}

/* =============================== Macros View ============================= */
function MacrosView() {
  const todayKey = new Date().toLocaleDateString("en-CA"); // "YYYY-MM-DD" local
  const me = useStoreQuery(storeQueries.currentUser);
  const plan = useStoreQuery(() => (me?.id ? storeQueries.macroPlan(me.id) : null));
  const targets = macroTargets(plan);
  const convexLog = useQuery(api.nutrition.todayLogs, { dateKey: todayKey });
  const logFoodMutation = useMutation(api.nutrition.logFood);
  const deleteLogMutation = useMutation(api.nutrition.deleteLog);
  const searchFoodAction = useAction(api.foodDb.searchIngredients);

  const [addOpen, setAddOpen] = useState(false);
  // The log row being replaced by an in-place edit, or null for a fresh add.
  // Edit used to DELETE first and reopen the sheet prefilled, so backing out --
  // or a reload mid-edit -- destroyed the entry with no undo and no confirm.
  // The old row is now removed only after the replacement has actually saved.
  const [editingLogId, setEditingLogId] = useState<Id<"nutritionLogs"> | null>(null);
  const [scanMode, setScanMode] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const [foodName, setFoodName] = useState("");
  const [foodKcal, setFoodKcal] = useState("");
  const [foodProtein, setFoodProtein] = useState("");
  const [foodCarbs, setFoodCarbs] = useState("");
  const [foodFat, setFoodFat] = useState("");
  const [foodServing, setFoodServing] = useState("1 serving");
  const [mealType, setMealType] = useState<"morning" | "midday" | "snack" | "evening">("snack");

  type Portion = { description: string; gramWeight: number };
  type Suggestion = {
    name: string;
    kcal: number; protein: number; carbs: number; fat: number;
    serving: string;
    kcalPer100g: number; proteinPer100g: number; carbsPer100g: number; fatPer100g: number; fiberPer100g: number;
    portions: Portion[];
    defaultPortion: Portion;
  };
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  // Per-serving recalculation state — only set when the user picked a
  // search suggestion (or scanned a barcode). Manual entries leave this
  // null and the macro inputs stay free-form.
  const [foodPortions, setFoodPortions] = useState<Portion[]>([]);
  const [selectedPortion, setSelectedPortion] = useState<Portion | null>(null);
  const [foodQty, setFoodQty] = useState("1");
  const [foodPer100g, setFoodPer100g] = useState<{ kcal: number; protein: number; carbs: number; fat: number; fiber: number } | null>(null);
  const [suggestOpen, setSuggestOpen] = useState(false);
  // "empty" = the search worked and found nothing. "error" = the search itself
  // is down. These must never look the same to her.
  const [searchState, setSearchState] = useState<"idle" | "searching" | "ok" | "empty" | "error">("idle");
  const skipSuggestRef = useRef(false); // true after scan/selection fills the field

  // Race-safe debounced food search via USDA (api.foodDb.searchIngredients).
  // Replaces Open Food Facts which surfaced messy international branded
  // products like "Raib banan sghir" before "Banana, raw".
  // - searchSeqRef discards stale responses when a newer query has been issued
  // - mountedRef prevents setState-on-unmounted React warnings if user
  //   navigates away mid-fetch
  const searchSeqRef = useRef(0);
  const searchMountedRef = useRef(true);
  useEffect(() => {
    searchMountedRef.current = true;
    return () => { searchMountedRef.current = false; };
  }, []);
  useEffect(() => {
    if (skipSuggestRef.current) { skipSuggestRef.current = false; return; }
    if (foodName.length < 2) { setSuggestions([]); setSuggestOpen(false); setSearchState("idle"); return; }
    const timer = setTimeout(async () => {
      const mySeq = ++searchSeqRef.current;
      setSearchState("searching");
      try {
        const results = await searchFoodAction({ query: foodName.trim(), limit: 7 });
        if (!searchMountedRef.current || mySeq !== searchSeqRef.current) return; // stale or unmounted
        const items: Suggestion[] = results.map((r) => {
          const factor = r.suggestedServingGrams / 100;
          const portions = (r.portions && r.portions.length > 0)
            ? r.portions
            : [{ description: "g", gramWeight: 1 }];
          const defaultPortion =
            portions.find((p) => p.description.toLowerCase() === r.suggestedServing.toLowerCase()) ??
            portions.find((p) => Math.abs(p.gramWeight - r.suggestedServingGrams) < 0.5) ??
            portions[0];
          return {
            name: r.description,
            kcal: Math.round(r.kcalPer100g * factor),
            protein: Math.round(r.proteinPer100g * factor),
            carbs: Math.round(r.carbsPer100g * factor),
            fat: Math.round(r.fatPer100g * factor),
            serving: r.suggestedServing,
            kcalPer100g: r.kcalPer100g,
            proteinPer100g: r.proteinPer100g,
            carbsPer100g: r.carbsPer100g,
            fatPer100g: r.fatPer100g,
            fiberPer100g: r.fiberPer100g,
            portions,
            defaultPortion,
          };
        });
        if (items.length > 0) { setSuggestions(items); setSuggestOpen(true); setSearchState("ok"); }
        else { setSuggestions([]); setSuggestOpen(false); setSearchState("empty"); }
      } catch (err) {
        console.error("[Garden] food search failed", err);
        Sentry.captureException(err, { tags: { subsystem: "garden" } });
        if (!searchMountedRef.current || mySeq !== searchSeqRef.current) return; // stale or unmounted
        // Say the search is down. An outage must never read as "no such food".
        setSuggestions([]);
        setSuggestOpen(false);
        setSearchState("error");
      }
    }, 320);
    return () => clearTimeout(timer);
  }, [foodName, searchFoodAction]);

  const mealEmoji: Record<string, string> = {
    morning: "🌅",
    midday: "🥗",
    snack: "🌿",
    evening: "🌙",
  };

  const handleAddFood = async (): Promise<void> => {
    if (!foodName.trim()) return;
    try {
      await logFoodMutation({
        dateKey: todayKey,
        meal: mealType,
        name: foodName.trim(),
        serving: foodServing || "1 serving",
        kcal: parseInt(foodKcal) || 0,
        protein: parseInt(foodProtein) || 0,
        carbs: foodCarbs ? parseInt(foodCarbs) : undefined,
        fat: foodFat ? parseInt(foodFat) : undefined,
      });
    } catch (err) {
      console.error("[Garden] logging food failed", err);
      Sentry.captureException(err, { tags: { subsystem: "garden" } });
      // Leave the sheet open with everything she typed still in it.
      toast({
        variant: "destructive",
        title: "That food didn't save",
        description: "It's not in today's log yet. Everything you typed is still here — check your connection and add it again.",
      });
      return;
    }
    // Replacement saved, so the original can go. If this delete fails she is
    // left with a duplicate rather than a hole -- the safe direction to fail.
    if (editingLogId) {
      try {
        await deleteLogMutation({ id: editingLogId });
      } catch (err) {
        console.error("[Garden] removing the edited food log failed", err);
        Sentry.captureException(err, { tags: { subsystem: "garden" } });
        toast({
          variant: "destructive",
          title: "Your edit saved twice",
          description: "The updated food is in today's log, but we couldn't remove the old one. Tap it and choose Remove.",
        });
      }
      setEditingLogId(null);
    }
    setFoodName("");
    setFoodKcal("");
    setFoodProtein("");
    setFoodCarbs("");
    setFoodFat("");
    setFoodServing("1 serving");
    setFoodPer100g(null);
    setFoodPortions([]);
    setSelectedPortion(null);
    setFoodQty("1");
    setAddOpen(false);
    setEditingLogId(null);
    setScanMode(false);
  };

  // Drop the per-100g recompute hook when the user edits the food name
  // by hand — they've abandoned the prior suggestion.
  useEffect(() => {
    if (skipSuggestRef.current) return; // came from suggestion click
    if (foodPer100g) {
      setFoodPer100g(null);
      setFoodPortions([]);
      setSelectedPortion(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foodName]);

  const handleDelete = async (id: Id<"nutritionLogs">): Promise<void> => {
    try {
      await deleteLogMutation({ id });
    } catch (err) {
      console.error("[Garden] removing a food log failed", err);
      Sentry.captureException(err, { tags: { subsystem: "garden" } });
      toast({
        variant: "destructive",
        title: "We couldn't remove that",
        description: "It's still in today's log. Check your connection and try removing it again.",
      });
      return;
    }
    setExpandedIdx(null);
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(30,74,53,0.16)",
    background: "rgba(255,255,255,0.85)",
    fontSize: 13.5,
    color: "var(--mnf-fd)" as string,
    outline: "none",
  };

  const log = convexLog ?? [];
  const totalKcal = log.reduce((s, r) => s + r.kcal, 0);
  const totalProtein = log.reduce((s, r) => s + r.protein, 0);
  const totalCarbs = log.reduce((s, r) => s + (r.carbs ?? 0), 0);
  const totalFat = log.reduce((s, r) => s + (r.fat ?? 0), 0);
  const kcalGoal = targets.kcal;
  const remaining = Math.max(0, kcalGoal - totalKcal);

  return (
    <>
      {/* Hero ring + macros */}
      <GlassCard padding={20} className="mnf-fade-up">
        <div className="flex items-center gap-4">
          <RingDial value={totalKcal} goal={kcalGoal} />
          <div className="flex-1">
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Tended today</p>
            <p className="mnf-serif" style={{ fontSize: 26, color: "var(--mnf-fd)", fontWeight: 500 }}>
              {totalKcal.toLocaleString()} <span style={{ fontSize: 14, color: "var(--mnf-tg)" }}>/ {kcalGoal.toLocaleString()} kcal</span>
            </p>
            <p className="text-xs mnf-body mt-0.5" style={{ color: "var(--mnf-tg)" }}>
              {remaining > 0 ? `${remaining} kcal still to nourish, sister` : "You've hit your goal today 🌿"}
            </p>
            <div className="flex gap-1.5 mt-3">
              <PillTag tone="forest">On track</PillTag>
              <PillTag tone="fiber"><Icon name="eco" size={11} /> 18g fiber</PillTag>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-5">
          {macroGoals.map((m) => {
            const value = m.key === "protein" ? totalProtein : m.key === "carbs" ? totalCarbs : totalFat;
            const goal = m.key === "protein" ? targets.protein : m.key === "carbs" ? targets.carbs : targets.fat;
            // Divide by HER goal, not the placeholder constant. The label
            // printed the real target while the percentage and the fill bar
            // divided by the static macroGoals value, so a sister on a 110g
            // protein target read "110 / 110g" beside a bar showing 73%. Two
            // numbers on one card disagreeing is how she stops trusting both.
            const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
            return (
              <div
                key={m.key}
                className="p-3"
                style={{ background: "rgba(255,255,255,0.6)", borderRadius: 16, border: "1px solid rgba(30,74,53,0.06)" }}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <Icon name={m.icon} size={16} color={m.color} />
                  <span className="text-[10px] mnf-body" style={{ color: "var(--mnf-tg)", fontWeight: 600 }}>
                    {pct}%
                  </span>
                </div>
                <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, color: "var(--mnf-fd)" }}>
                  {value}<span style={{ fontSize: 10, color: "var(--mnf-tg)" }}>g</span>
                </p>
                <p className="text-[10px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.1em" }}>
                  {m.label} · {goal}g
                </p>
                <div
                  className="mt-2 h-1 rounded-full overflow-hidden"
                  style={{ background: "rgba(30,74,53,0.08)" }}
                >
                  <div style={{ width: `${pct}%`, height: "100%", background: m.color, borderRadius: 999 }} />
                </div>
              </div>
            );
          })}
        </div>
      </GlassCard>

      {/* Quick add */}
      <div className="grid grid-cols-2 gap-2.5 mnf-fade-up">
        <Btn
          variant="primary"
          block
          style={{ height: 50 }}
          onClick={() => { setAddOpen(true); setScanMode(false); }}
          data-testid="button-add-food"
        >
          <Icon name="add" size={18} /> Add to Garden 🌱
        </Btn>
        <Btn
          variant="secondary"
          block
          style={{ height: 50 }}
          onClick={() => { setAddOpen(true); setScanMode(true); }}
          data-testid="button-scan-label"
        >
          <Icon name="qr_code_scanner" size={18} /> Scan label
        </Btn>
      </div>

      {/* Add food panel */}
      {addOpen && (
        <GlassCard
          padding={18}
          className="mnf-fade-up"
          style={{ border: "1px solid rgba(30,74,53,0.16)" }}
        >
          <div className="flex items-center justify-between mb-3">
            <p className="mnf-eyebrow">{scanMode ? "Scan or enter label info" : "Add to your garden"}</p>
            <button
              onClick={() => {
                setAddOpen(false);
                setEditingLogId(null);
                setScanMode(false);
                setFoodPer100g(null);
                setFoodPortions([]);
                setSelectedPortion(null);
                setFoodQty("1");
              }}
              style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}
              aria-label="Close"
            >
              <Icon name="close" size={18} color="var(--mnf-tg)" />
            </button>
          </div>

          {scanMode && (
            <BarcodeScanner
              onResult={(food: ScannedFood) => {
                skipSuggestRef.current = true;
                setFoodName(food.name);
                setFoodKcal(String(food.kcal));
                setFoodProtein(String(food.protein));
                // Carbs and fat were never set from a scan, so both rings
                // under-counted with nothing on screen admitting it.
                setFoodCarbs(String(food.carbs));
                setFoodFat(String(food.fat));
                setFoodServing(food.serving);
                // Per-100g values make the serving-size dropdown work for a
                // scanned item exactly as it does for a searched one.
                setFoodPer100g(
                  food.per100
                    ? { ...food.per100, fiber: 0 }
                    : null,
                );
                setSuggestOpen(false);
                setScanMode(false);
              }}
              onManual={() => setScanMode(false)}
            />
          )}

          <div className="space-y-2.5" style={{ display: scanMode ? "none" : undefined }}>
            {/* Meal type */}
            <div>
              <p className="mnf-eyebrow mb-1.5">Meal</p>
              <div className="flex gap-1.5 flex-wrap">
                {(["morning", "midday", "snack", "evening"] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMealType(m)}
                    className="mnf-body"
                    style={{
                      height: 32,
                      padding: "0 12px",
                      borderRadius: 999,
                      background: mealType === m ? "var(--mnf-f)" : "transparent",
                      color: mealType === m ? "#F5F0E8" : "var(--mnf-fd)",
                      border: mealType === m ? "1px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.18)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                      textTransform: "capitalize",
                    }}
                  >
                    {mealEmoji[m]} {m}
                  </button>
                ))}
              </div>
            </div>

            {/* Food name with typeahead */}
            <div style={{ position: "relative" }}>
              <p className="mnf-eyebrow mb-1.5">Food name</p>
              <input
                type="text"
                value={foodName}
                onChange={(e) => { setFoodName(e.target.value); setSuggestOpen(true); }}
                onFocus={() => { if (suggestions.length > 0) setSuggestOpen(true); }}
                onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                placeholder="e.g. Greek yogurt with honey"
                className="mnf-body"
                style={inputStyle}
                autoFocus
                autoComplete="off"
              />
              {searchState === "error" && (
                <p
                  role="alert"
                  data-testid="text-food-search-down"
                  className="mnf-body"
                  style={{ fontSize: 11.5, lineHeight: 1.45, color: "var(--mnf-t)", marginTop: 6 }}
                >
                  Food lookup isn&apos;t answering right now — this isn&apos;t about your food. Type the name and numbers in yourself and it will still save.
                </p>
              )}
              {searchState === "empty" && (
                <p
                  data-testid="text-food-search-empty"
                  className="mnf-body"
                  style={{ fontSize: 11.5, lineHeight: 1.45, color: "var(--mnf-tg)", marginTop: 6 }}
                >
                  No food matched that. Try a simpler word, or just type the numbers in yourself.
                </p>
              )}
              {suggestOpen && suggestions.length > 0 && (
                <ul
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    background: "#fff",
                    border: "1px solid rgba(30,74,53,0.18)",
                    borderRadius: 12,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                    marginTop: 4,
                    padding: "4px 0",
                    listStyle: "none",
                    maxHeight: 220,
                    overflowY: "auto",
                  }}
                >
                  {suggestions.map((s, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        onMouseDown={() => {
                          skipSuggestRef.current = true;
                          setFoodName(s.name);
                          setFoodPer100g({ kcal: s.kcalPer100g, protein: s.proteinPer100g, carbs: s.carbsPer100g, fat: s.fatPer100g, fiber: s.fiberPer100g });
                          setFoodPortions(s.portions);
                          setSelectedPortion(s.defaultPortion);
                          const initialQty = s.defaultPortion.description === "g" ? "100" : "1";
                          setFoodQty(initialQty);
                          const grams = (parseFloat(initialQty) || 1) * s.defaultPortion.gramWeight;
                          const factor = grams / 100;
                          setFoodKcal(String(Math.round(s.kcalPer100g * factor)));
                          setFoodProtein(String(Math.round(s.proteinPer100g * factor)));
                          setFoodCarbs(String(Math.round(s.carbsPer100g * factor)));
                          setFoodFat(String(Math.round(s.fatPer100g * factor)));
                          setFoodServing(`${initialQty} ${s.defaultPortion.description}`);
                          setSuggestOpen(false);
                        }}
                        className="mnf-body"
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "9px 14px",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 13,
                          color: "var(--mnf-fd)",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {s.name}
                        </span>
                        <span style={{ fontSize: 11.5, color: "var(--mnf-tg)", whiteSpace: "nowrap", flexShrink: 0 }}>
                          {s.kcal} kcal · {s.protein}g protein
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Serving size + quantity — only shown when a search suggestion
                is selected, so the macros recalculate from USDA per-100g
                rather than freezing on the suggested portion. */}
            {foodPer100g && foodPortions.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="mnf-eyebrow mb-1.5">Serving size</p>
                  <select
                    value={selectedPortion ? String(foodPortions.findIndex((p) => p === selectedPortion)) : "0"}
                    onChange={(e) => {
                      const idx = parseInt(e.target.value, 10);
                      const next = foodPortions[idx];
                      if (!next) return;
                      setSelectedPortion(next);
                      const nextQty = next.description === "g" ? "100" : "1";
                      setFoodQty(nextQty);
                      const grams = (parseFloat(nextQty) || 1) * next.gramWeight;
                      const factor = grams / 100;
                      setFoodKcal(String(Math.round(foodPer100g.kcal * factor)));
                      setFoodProtein(String(Math.round(foodPer100g.protein * factor)));
                      setFoodCarbs(String(Math.round(foodPer100g.carbs * factor)));
                      setFoodFat(String(Math.round(foodPer100g.fat * factor)));
                      setFoodServing(`${nextQty} ${next.description}`);
                    }}
                    className="mnf-body"
                    style={inputStyle}
                  >
                    {foodPortions.map((p, i) => (
                      <option key={i} value={String(i)}>
                        {p.description === "g" ? "g (enter grams)" : `${p.description} — ${Math.round(p.gramWeight)}g`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="mnf-eyebrow mb-1.5">{selectedPortion?.description === "g" ? "Amount (g)" : "Quantity"}</p>
                  <input
                    type="number"
                    value={foodQty}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setFoodQty(raw);
                      const qty = parseFloat(raw);
                      if (!Number.isFinite(qty) || !selectedPortion) return;
                      const grams = qty * selectedPortion.gramWeight;
                      const factor = grams / 100;
                      setFoodKcal(String(Math.round(foodPer100g.kcal * factor)));
                      setFoodProtein(String(Math.round(foodPer100g.protein * factor)));
                      setFoodCarbs(String(Math.round(foodPer100g.carbs * factor)));
                      setFoodFat(String(Math.round(foodPer100g.fat * factor)));
                      setFoodServing(`${raw} ${selectedPortion.description}`);
                    }}
                    min={selectedPortion?.description === "g" ? 1 : 0.25}
                    step={selectedPortion?.description === "g" ? 1 : 0.25}
                    className="mnf-body"
                    style={inputStyle}
                  />
                </div>
              </div>
            )}

            {/* Macros grid */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="mnf-eyebrow mb-1.5">Calories</p>
                <input
                  type="number"
                  value={foodKcal}
                  onChange={(e) => setFoodKcal(e.target.value)}
                  placeholder="0"
                  className="mnf-body"
                  style={inputStyle}
                  min={0}
                />
              </div>
              <div>
                <p className="mnf-eyebrow mb-1.5">Protein (g)</p>
                <input
                  type="number"
                  value={foodProtein}
                  onChange={(e) => setFoodProtein(e.target.value)}
                  placeholder="0"
                  className="mnf-body"
                  style={inputStyle}
                  min={0}
                />
              </div>
              <div>
                <p className="mnf-eyebrow mb-1.5">Carbs (g)</p>
                <input
                  type="number"
                  value={foodCarbs}
                  onChange={(e) => setFoodCarbs(e.target.value)}
                  placeholder="0"
                  className="mnf-body"
                  style={inputStyle}
                  min={0}
                />
              </div>
              <div>
                <p className="mnf-eyebrow mb-1.5">Fat (g)</p>
                <input
                  type="number"
                  value={foodFat}
                  onChange={(e) => setFoodFat(e.target.value)}
                  placeholder="0"
                  className="mnf-body"
                  style={inputStyle}
                  min={0}
                />
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <Btn variant="secondary" onClick={() => {
                setAddOpen(false);
                setEditingLogId(null);
                setScanMode(false);
                setFoodPer100g(null);
                setFoodPortions([]);
                setSelectedPortion(null);
                setFoodQty("1");
              }}>
                Cancel
              </Btn>
              <Btn
                variant="primary"
                onClick={() => { void handleAddFood(); }}
                style={{ flex: 1 }}
                disabled={!foodName.trim()}
              >
                <Icon name="add" size={14} /> Plant it
              </Btn>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Today's log */}
      {(() => {
        const log = convexLog ?? [];
        const fmtTime = (ms: number) => {
          const d = new Date(ms);
          const h = d.getHours(); const m = String(d.getMinutes()).padStart(2, "0");
          return `${((h % 12) || 12)}:${m}${h >= 12 ? "p" : "a"}`;
        };
        const emoji: Record<string, string> = { morning: "🌅", midday: "🥗", snack: "🌿", evening: "🌙" };
        return (
          <>
            <SectionTitle
              eyebrow="Today's harvest"
              title="What you've nourished"
              trailing={`${log.length} entries`}
            />
            <GlassCard padding={0} className="mnf-fade-up">
              {log.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
                    Nothing logged yet. Tap "Add to Garden" above to begin.
                  </p>
                </div>
              ) : (
                <ul className="divide-y" style={{ borderColor: "rgba(30,74,53,0.06)" }}>
                  {log.map((row, i) => (
                    <li key={row._id}>
                      <button
                        className="flex items-center gap-3 px-4 py-3 w-full text-left hover-elevate"
                        style={{ background: "transparent", border: "none", cursor: "pointer" }}
                        onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
                        data-testid={`log-entry-${i}`}
                      >
                        <span style={{ fontSize: 22 }}>{emoji[row.meal] ?? "🍽️"}</span>
                        <div className="flex-1 min-w-0">
                          <p className="mnf-body truncate" style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mnf-tx)" }}>
                            {row.name}
                          </p>
                          <p className="text-[11px] mnf-body" style={{ color: "var(--mnf-tg)" }}>
                            {row.serving} · {fmtTime(row.loggedAt)}
                          </p>
                        </div>
                        <div className="text-right mr-1">
                          <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
                            {row.kcal}
                          </p>
                          <p className="text-[10px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.1em" }}>
                            kcal · {row.protein}g pro
                          </p>
                        </div>
                        <Icon name={expandedIdx === i ? "expand_less" : "expand_more"} size={18} color="var(--mnf-tg)" />
                      </button>

                      {expandedIdx === i && (
                        <div className="px-4 pb-3" style={{ borderTop: "1px solid rgba(30,74,53,0.06)", background: "rgba(30,74,53,0.03)" }}>
                          <div className="flex items-center gap-2 pt-3">
                            <Btn variant="secondary" size="sm" onClick={() => {
                              setFoodName(row.name);
                              setFoodKcal(String(row.kcal));
                              setFoodProtein(String(row.protein));
                              setFoodServing(row.serving);
                              setFoodCarbs(row.carbs != null ? String(row.carbs) : "");
                              setFoodFat(row.fat != null ? String(row.fat) : "");
                              setMealType(row.meal);
                              setEditingLogId(row._id);
                              setAddOpen(true);
                            }}>
                              <Icon name="edit" size={13} /> Edit
                            </Btn>
                            <Btn variant="ghost" size="sm" onClick={() => { void handleDelete(row._id); }}
                              style={{ color: "#C0784A", borderColor: "rgba(192,120,74,0.24)" }}>
                              <Icon name="delete" size={13} /> Remove
                            </Btn>
                            <div className="flex-1" />
                            <p className="text-[11px] mnf-body" style={{ color: "var(--mnf-tg)" }}>
                              Logged at {fmtTime(row.loggedAt)}
                            </p>
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </GlassCard>
          </>
        );
      })()}

      {/* Coach note */}
      <GlassCard
        padding={18}
        className="mnf-fade-up"
        style={{
          background: "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(245,240,232,0.65) 100%)",
        }}
      >
        <div className="flex items-start gap-3">
          <Avatar initials="K" size={40} ringColor="rgba(192,120,74,0.6)" />
          <div className="flex-1">
            <p className="mnf-eyebrow">Coach Kiley · 10:42a</p>
            <p className="mnf-serif italic mt-1" style={{ fontSize: 14.5, lineHeight: 1.5, color: "var(--mnf-fd)" }}>
              "Your protein is climbing beautifully today, beloved. A handful of almonds before
              your evening walk would round it off perfectly."
            </p>
          </div>
        </div>
      </GlassCard>

      <div className="h-2" />
    </>
  );
}

/* =============================== Ring Dial =============================== */
function RingDial({ value, goal }: { value: number; goal: number }) {
  const r = 38;
  const c = 2 * Math.PI * r;
  const pct = Math.min(1, value / goal);
  return (
    <div style={{ width: 110, height: 110, position: "relative", flexShrink: 0 }}>
      <svg viewBox="0 0 100 100" width="110" height="110">
        <circle cx="50" cy="50" r={r} stroke="rgba(30,74,53,0.10)" strokeWidth="9" fill="none" />
        <circle
          cx="50" cy="50" r={r}
          stroke="url(#ringGrad)"
          strokeWidth="9"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${c * pct} ${c}`}
          transform="rotate(-90 50 50)"
        />
        <defs>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#1E4A35" />
            <stop offset="100%" stopColor="#C0784A" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="mnf-serif italic" style={{ fontSize: 11, color: "var(--mnf-t)", fontWeight: 600, letterSpacing: "0.1em" }}>
          {Math.round(pct * 100)}%
        </p>
        <p className="mnf-serif" style={{ fontSize: 18, color: "var(--mnf-fd)", fontWeight: 600 }}>
          {value}
        </p>
        <p className="text-[9px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.14em" }}>
          of {goal}
        </p>
      </div>
    </div>
  );
}

/* =============================== Portions View ========================== */
function PortionsView() {
  const portionData = [
    { hand: "Palm",   food: "Protein",      icon: "back_hand",  key: "protein", goal: 4, init: 3 },
    { hand: "Fist",   food: "Vegetables",   icon: "front_hand", key: "veggies", goal: 5, init: 2 },
    { hand: "Cupped", food: "Carbs",        icon: "pan_tool",   key: "carbs",   goal: 3, init: 2 },
    { hand: "Thumb",  food: "Healthy fats", icon: "thumb_up",   key: "fats",    goal: 4, init: 2 },
  ];

  const [counts, setCounts] = useState<Record<string, number>>(
    () => Object.fromEntries(portionData.map((p) => [p.key, p.init])),
  );
  const [saved, setSaved] = useState(false);

  const adjust = (key: string, delta: number) => {
    setSaved(false);
    setCounts((c) => ({ ...c, [key]: Math.max(0, c[key] + delta) }));
  };

  /**
   * Hand-portion counts are session-only.
   *
   * This used to show a "Saved" confirmation and persist nothing — the counts
   * reset to the hardcoded defaults on the next mount. There is no Convex table
   * for hand portions, so rather than confirm a save that never happened, the
   * UI now says plainly that the count is for today's session.
   */
  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  };

  const btnBase = {
    width: 26,
    height: 26,
    borderRadius: 999,
    border: "1px solid rgba(30,74,53,0.18)",
    background: "rgba(255,255,255,0.8)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 700,
    color: "var(--mnf-f)" as string,
    flexShrink: 0,
  };

  return (
    <>
      <GlassCard padding={18} className="mnf-fade-up">
        <p className="mnf-eyebrow">Today's hand portions</p>
        <p className="mnf-serif italic mt-1" style={{ fontSize: 14, color: "var(--mnf-tg)" }}>
          A gentler way to listen to your body.
        </p>
        <div className="grid grid-cols-2 gap-3 mt-4">
          {portionData.map((p) => {
            const count = counts[p.key];
            const filled = count >= p.goal;
            return (
              <div
                key={p.key}
                className="p-3.5"
                style={{
                  background: filled ? "rgba(168,212,184,0.18)" : "rgba(255,255,255,0.65)",
                  border: filled
                    ? "1px solid rgba(30,74,53,0.14)"
                    : "1px solid rgba(30,74,53,0.06)",
                  borderRadius: 18,
                  transition: "background 200ms",
                }}
              >
                <div
                  className="inline-flex items-center justify-center mb-2"
                  style={{
                    width: 36, height: 36, borderRadius: 12,
                    background: filled ? "rgba(30,74,53,0.12)" : "rgba(192,120,74,0.10)",
                    color: filled ? "#1E4A35" : "var(--mnf-t)",
                  }}
                >
                  <Icon name={filled ? "check_circle" : p.icon} size={20} />
                </div>
                <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
                  {p.hand}
                </p>
                <p className="text-[11px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.1em" }}>
                  {p.food}
                </p>
                {/* stepper */}
                <div className="flex items-center gap-2 mt-2.5">
                  <button
                    style={btnBase as React.CSSProperties}
                    onClick={() => adjust(p.key, -1)}
                    aria-label={`Remove one ${p.food} portion`}
                    disabled={count === 0}
                  >
                    −
                  </button>
                  <span
                    className="mnf-serif"
                    style={{ fontSize: 15, fontWeight: 600, color: "var(--mnf-fd)", minWidth: 18, textAlign: "center" }}
                  >
                    {count}
                  </span>
                  <button
                    style={btnBase as React.CSSProperties}
                    onClick={() => adjust(p.key, 1)}
                    aria-label={`Add one ${p.food} portion`}
                  >
                    +
                  </button>
                  <span className="text-[11px] mnf-body ml-auto" style={{ color: filled ? "#1E4A35" : "var(--mnf-tg)", fontWeight: 600 }}>
                    of {p.goal}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </GlassCard>

      {saved ? (
        <div
          className="flex items-center gap-2 px-4 py-3 mnf-fade-up"
          style={{
            background: "rgba(168,212,184,0.22)",
            border: "1px solid rgba(30,74,53,0.14)",
            borderRadius: 14,
          }}
        >
          <Icon name="check_circle" size={18} color="#1E4A35" />
          <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)" }}>
            Portions saved for today, sister.
          </p>
        </div>
      ) : (
        <Btn
          variant="primary"
          block
          style={{ height: 50 }}
          onClick={handleSave}
          data-testid="button-save-portions"
        >
          <Icon name="check" size={18} /> Save today's portions
        </Btn>
      )}
    </>
  );
}

/* =============================== Plan View =============================== */
function PlanView({ day, setDay }: { day: number; setDay: (n: number) => void }) {
  const weekDates = getWeekDates();

  // Planned recipes for the active day (from "Add to plan" on RecipeDetail).
  // dateKey is the ISO date string in user's local time — en-CA gives YYYY-MM-DD.
  const me = useStoreQuery(storeQueries.currentUser);
  const planTargets = macroTargets(
    useStoreQuery(() => (me?.id ? storeQueries.macroPlan(me.id) : null)),
  );
  const dateKey = weekDates[day].toLocaleDateString("en-CA");
  const plannedSummaries = useQuery(
    api.recipesDb.plannedMealsOnDate,
    me?.id ? { userId: me.id, dateKey } : "skip",
  );
  const removeFromMealPlan = useMutation(api.recipesDb.removeFromMealPlan);

  const [expandedSlot, setExpandedSlot] = useState<number | null>(null);
  const [slotStatuses, setSlotStatuses] = useState<Record<string, SlotStatus>>({});
  const [shoppingOpen, setShoppingOpen] = useState(false);
  const [addSlotOpen, setAddSlotOpen] = useState(false);

  // Add slot form state
  const [newSlotName, setNewSlotName] = useState("Afternoon snack");
  const [newSlotTime, setNewSlotTime] = useState("2:30 pm");
  const [newSlotKcal, setNewSlotKcal] = useState("200");

  // Grocery list — per-week, persisted in Convex. weekStart matches the
  // week's Monday in en-CA YYYY-MM-DD form so it lines up with dateKey.
  const weekStart = weekDates[0].toLocaleDateString("en-CA");
  const groceryItems = useQuery(
    api.groceryDb.listForWeek,
    me?.id && shoppingOpen ? { userId: me.id, weekStart } : "skip",
  );
  const pullFromPlannedMeals = useMutation(api.groceryDb.pullFromPlannedMeals);
  const addCustomGroceryItem = useMutation(api.groceryDb.addCustomItem);
  const toggleGroceryChecked = useMutation(api.groceryDb.toggleChecked);
  const deleteGroceryItem = useMutation(api.groceryDb.deleteItem);
  const clearCheckedGrocery = useMutation(api.groceryDb.clearChecked);

  const [pulling, setPulling] = useState(false);
  const [pullStatus, setPullStatus] = useState<string | null>(null);
  const [customName, setCustomName] = useState("");
  const [customQty, setCustomQty] = useState("");
  const [customUnit, setCustomUnit] = useState("");

  const handlePullPlannedMeals = async () => {
    if (!me?.id || pulling) return;
    setPulling(true);
    setPullStatus(null);
    try {
      const r = await pullFromPlannedMeals({ userId: me.id, weekStart });
      const total = r.added + r.merged;
      setPullStatus(
        total === 0
          ? r.skippedSeeds > 0
            ? "Pulled — but planned recipes are seed-only this week."
            : "Nothing to pull. Add recipes to this week's plan first."
          : `Pulled ${total} ingredient${total === 1 ? "" : "s"} from your plan.`,
      );
    } catch (err) {
      console.error("[Garden] pulling ingredients from the plan failed", err);
      Sentry.captureException(err, { tags: { subsystem: "garden" } });
      setPullStatus("Nothing was pulled from your plan. Check your connection and try again.");
      toast({
        variant: "destructive",
        title: "We couldn't pull from your plan",
        description: "Your grocery list is unchanged. Check your connection and try again.",
      });
    } finally {
      setPulling(false);
    }
  };

  const handleAddCustom = async () => {
    if (!me?.id) return;
    const name = customName.trim();
    if (!name) return;
    const qtyNum = customQty ? parseFloat(customQty) : undefined;
    try {
      await addCustomGroceryItem({
        userId: me.id,
        weekStart,
        displayName: name,
        qty: Number.isFinite(qtyNum) ? qtyNum : undefined,
        unit: customUnit.trim() || undefined,
      });
      setCustomName("");
      setCustomQty("");
      setCustomUnit("");
    } catch (err) {
      console.error("[Garden] adding a grocery item failed", err);
      Sentry.captureException(err, { tags: { subsystem: "garden" } });
      // Inputs stay filled on purpose — nothing was added to the list.
      toast({
        variant: "destructive",
        title: `"${name}" wasn't added`,
        description: "It's not on your grocery list yet. Check your connection and add it again.",
      });
    }
  };

  const plan = planByDay[day];
  const plannedTotal = plan.slots.reduce((s, x) => s + x.plannedKcal, 0);
  const eatenTotal = plan.slots.reduce((s, x) => s + x.eatenKcal, 0);
  const fillPct = Math.min(100, Math.round((eatenTotal / planTargets.kcal) * 100));
  const remaining = Math.max(0, planTargets.kcal - eatenTotal);
  const todayIdx = todayIndex();
  const isPast = day < todayIdx;
  const isToday = day === todayIdx;
  const isFuture = day > todayIdx;

  const macroLines: { key: keyof typeof MACRO_GOAL; label: string; color: string; icon: string }[] = [
    { key: "protein", label: "Protein", color: "#C0784A", icon: "egg_alt" },
    { key: "carbs",   label: "Carbs",   color: "#A07820", icon: "grain" },
    { key: "fat",     label: "Fat",     color: "#7F77DD", icon: "water_drop" },
    { key: "fiber",   label: "Fiber",   color: "#A8D4B8", icon: "eco" },
  ];

  const hasShortfall = !isFuture && Object.values(plan.shortfall).some((v) => v > 0);

  const slotKey = (slotIdx: number) => `${day}-${slotIdx}`;
  const getStatus = (s: PlanSlot, idx: number): SlotStatus =>
    slotStatuses[slotKey(idx)] ?? s.status;

  /**
   * Mark a slot on the WEEKLY PLAN.
   *
   * This used to call the real `nutrition.logFood` mutation. The plan it marks
   * (`planByDay`) is seven days of hardcoded demo meals — "Sheet-pan salmon +
   * roots", 610 kcal — that the user never planned and never ate. Tapping
   * "eaten" inserted a real nutritionLogs row, so invented food counted toward
   * her actual calorie ring on Garden and Macros.
   *
   * In a nutrition tracker that is the core promise inverted: the number she
   * trusts was partly fabricated. The rollback and toast handling around the
   * write were meticulous, which is precisely why the code read as correct.
   *
   * The status is now local display state only, and the plan is labelled as a
   * sample. Real meals are logged from the food search and the barcode scanner,
   * and real planned recipes live in `plannedMeals` — both of which persist
   * properly. A genuine weekly planner is a feature to build, not to simulate.
   */
  const markSlot = (idx: number, status: SlotStatus) => {
    setSlotStatuses((prev) => ({ ...prev, [slotKey(idx)]: status }));
    setExpandedSlot(null);
  };


  const inputStyle = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid rgba(30,74,53,0.16)",
    background: "rgba(255,255,255,0.85)",
    fontSize: 13,
    color: "var(--mnf-fd)" as string,
    outline: "none",
  };

  return (
    <>
      <SectionTitle
        eyebrow="This week's harvest"
        title="Family meal plan"
        trailing="Mon-first"
      />

      <div className="flex gap-1.5 overflow-x-auto mnf-scroll -mx-1 px-1">
        {planDays.map((d, i) => {
          const active = day === i;
          return (
            <button
              key={d}
              onClick={() => { setDay(i); setExpandedSlot(null); }}
              data-testid={`day-${d.toLowerCase()}`}
              className="flex-shrink-0 flex flex-col items-center transition-all hover-elevate"
              style={{
                width: 52, height: 64, borderRadius: 16,
                background: active ? "var(--mnf-f)" : "rgba(255,255,255,0.65)",
                color: active ? "#F5F0E8" : "var(--mnf-tx)",
                border: active ? "none" : "1px solid rgba(30,74,53,0.06)",
                cursor: "pointer",
              }}
            >
              <span className="mnf-body text-[10px] uppercase mt-2" style={{ letterSpacing: "0.12em", opacity: 0.8 }}>
                {d}
              </span>
              <span className="mnf-serif" style={{ fontSize: 18, fontWeight: 500, marginTop: 2 }}>
                {weekDates[i].getDate()}
              </span>
            </button>
          );
        })}
      </div>

      {/* Planned recipes for this day (from RecipeDetail "Add to plan"). */}
      {plannedSummaries && plannedSummaries.length > 0 && (
        <div className="mnf-fade-up">
          <SectionTitle
            eyebrow="From your plan"
            title="Planted recipes"
            trailing={`${plannedSummaries.length} ${plannedSummaries.length === 1 ? "recipe" : "recipes"}`}
          />
          <div className="space-y-2">
            {plannedSummaries.map((s) => {
              // Resolve title: persistedRecipe (Convex) first, in-memory seed fallback.
              const persisted = s.persistedRecipe as { title?: string } | null;
              const seed = persisted ? null : storeQueries.recipeById(s.recipeId);
              const title = persisted?.title ?? seed?.title ?? "Recipe";
              const mealLabel = s.meal.charAt(0).toUpperCase() + s.meal.slice(1);
              const mealEmoji =
                s.meal === "morning" ? "🌅" :
                s.meal === "midday"  ? "🥗" :
                s.meal === "snack"   ? "🌿" :
                                       "🌙";
              return (
                <div
                  key={s.planId as unknown as string}
                  className="flex items-center gap-3 hover-elevate"
                  style={{
                    padding: "10px 12px",
                    borderRadius: 14,
                    background: "rgba(127,119,221,0.06)",
                    border: "1px solid rgba(127,119,221,0.18)",
                  }}
                >
                  <span style={{ fontSize: 18 }} aria-hidden>{mealEmoji}</span>
                  <Link
                    href={`/nourish/${s.recipeId}`}
                    className="no-underline flex-1 min-w-0"
                  >
                    <p
                      className="mnf-body truncate"
                      style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mnf-fd)" }}
                    >
                      {title}
                    </p>
                    <p
                      className="mnf-body"
                      style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 2 }}
                    >
                      {mealLabel} · {s.servings} {s.servings === 1 ? "serving" : "servings"}
                    </p>
                  </Link>
                  <button
                    data-testid={`button-remove-plan-${s.planId}`}
                    aria-label={`Remove ${title} from ${mealLabel}`}
                    onClick={() => {
                      void removeFromMealPlan({ planId: s.planId }).catch((err: unknown) => {
                          reportWriteFailure(
                            "meal-plan", "removing a planned meal", err,
                            "We couldn't remove that meal",
                            "It's still on your plan. Check your connection and try again.",
                          );
                        });
                    }}
                    className="hover-elevate shrink-0"
                    style={{
                      width: 30, height: 30, borderRadius: 999,
                      background: "rgba(30,74,53,0.06)",
                      color: "var(--mnf-tg)",
                      border: "none",
                      cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <Icon name="close" size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Day plan card */}
      <GlassCard padding={0} className="mnf-fade-up" data-testid={`plan-day-${day}`}>
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: "1px solid rgba(30,74,53,0.06)" }}
        >
          <div className="min-w-0">
            <p className="mnf-eyebrow">
              {weekDates[day].toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric" })}{isToday && " · Today"}
            </p>
            <p className="mnf-serif truncate" style={{ fontSize: 17, fontWeight: 500, color: "var(--mnf-fd)" }}>
              {plan.theme}
            </p>
          </div>
          {/*
              "AI fill" was a button that set a flag, flipped its own label to
              "Filled!" for 2.4 seconds, and changed nothing. Removed rather
              than left in: a control that performs a success animation over no
              work teaches a tester the app is unreliable everywhere else.
            */}
        </div>

        {/* Daily summary */}
        <div
          className="px-4 py-3.5"
          style={{
            background: "linear-gradient(135deg, rgba(30,74,53,0.04) 0%, rgba(192,120,74,0.04) 100%)",
            borderBottom: "1px solid rgba(30,74,53,0.06)",
          }}
        >
          <div className="flex items-baseline justify-between mb-2">
            <div>
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
                {isFuture ? "Planned for the day" : "Mom's nourishment"}
              </p>
              <p className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.1 }}>
                {isFuture ? plannedTotal.toLocaleString() : eatenTotal.toLocaleString()}
                <span style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
                  {" "}/ {planTargets.kcal.toLocaleString()} kcal
                </span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.12em" }}>
                {isFuture ? "Plan total" : "Filled"}
              </p>
              <p className="mnf-serif" style={{ fontSize: 18, fontWeight: 600, color: "var(--mnf-f)" }}>
                {isFuture ? `${Math.round((plannedTotal / planTargets.kcal) * 100)}%` : `${fillPct}%`}
              </p>
            </div>
          </div>

          {/* Fill bar */}
          <div
            className="h-2 rounded-full overflow-hidden relative"
            style={{ background: "rgba(30,74,53,0.08)" }}
          >
            <div
              style={{
                width: `${isFuture ? Math.min(100, Math.round((plannedTotal / planTargets.kcal) * 100)) : fillPct}%`,
                height: "100%",
                background: isFuture
                  ? "linear-gradient(90deg, rgba(127,119,221,0.6), #7F77DD)"
                  : "linear-gradient(90deg, #1E4A35, #C0784A)",
                borderRadius: 999,
                transition: "width 320ms ease",
              }}
            />
          </div>

          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] mnf-body italic" style={{ color: "var(--mnf-tg)" }}>
              {isFuture
                ? plan.note
                : remaining > 0
                ? `${remaining} kcal still to nourish, sister.`
                : "Goal nourished, beloved."}
            </p>
            {!isFuture && (
              <p className="text-[10px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.1em" }}>
                Plan {plannedTotal.toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {/* Slots */}
        <ul>
          {plan.slots.map((s, i) => {
            const status = getStatus(s, i);
            const sv = statusVisuals(status);
            const isFutureSlot = status === "upcoming" || status === "planned";
            const slotPct = s.plannedKcal > 0 ? Math.min(100, Math.round((s.eatenKcal / s.plannedKcal) * 100)) : 0;
            const isExpanded = expandedSlot === i;

            return (
              <li
                key={i}
                style={{ borderBottom: i < plan.slots.length - 1 ? "1px solid rgba(30,74,53,0.06)" : "none" }}
                data-testid={`slot-${s.slot.toLowerCase()}-${day}`}
              >
                <button
                  className="flex items-center gap-3 px-4 py-3 w-full text-left hover-elevate"
                  style={{ background: "transparent", border: "none", cursor: "pointer" }}
                  onClick={() => setExpandedSlot(isExpanded ? null : i)}
                  aria-expanded={isExpanded}
                >
                  <div
                    className="flex flex-col items-center justify-center relative"
                    style={{
                      width: 44, height: 44, borderRadius: 14,
                      background: sv.bg, color: sv.color, flexShrink: 0,
                    }}
                  >
                    <span className="text-[9px] mnf-body uppercase" style={{ letterSpacing: "0.1em", fontWeight: 700 }}>
                      {s.slot.slice(0, 3)}
                    </span>
                    <span
                      style={{
                        position: "absolute",
                        bottom: -3, right: -3,
                        width: 16, height: 16, borderRadius: 999,
                        background: "#F5F0E8",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: `1.5px solid ${sv.color}`,
                      }}
                    >
                      <Icon name={sv.icon} size={10} color={sv.color} />
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p
                      className="mnf-body truncate"
                      style={{
                        fontSize: 13.5, fontWeight: 600,
                        color: status === "skipped" ? "var(--mnf-tg)" : "var(--mnf-tx)",
                        textDecoration: status === "skipped" ? "line-through" : "none",
                      }}
                    >
                      {s.food}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[11px] mnf-body" style={{ color: "var(--mnf-tg)" }}>
                        {s.time}
                      </span>
                      <span style={{ color: "var(--mnf-tg)", fontSize: 9 }}>·</span>
                      {isFutureSlot ? (
                        <span className="text-[11px] mnf-body" style={{ color: "var(--mnf-tg)" }}>
                          {s.plannedKcal} kcal planned
                        </span>
                      ) : (
                        <span className="text-[11px] mnf-body" style={{ color: sv.color, fontWeight: 600 }}>
                          {s.eatenKcal} of {s.plannedKcal} kcal
                        </span>
                      )}
                    </div>
                    {!isFutureSlot && (
                      <div
                        className="mt-1.5 h-1 rounded-full overflow-hidden"
                        style={{ background: "rgba(30,74,53,0.06)", maxWidth: 140 }}
                      >
                        <div style={{ width: `${slotPct}%`, height: "100%", background: sv.color, borderRadius: 999 }} />
                      </div>
                    )}
                  </div>

                  {s.family && <PillTag tone="coral"><Icon name="diversity_1" size={11} /> Family</PillTag>}
                  <Icon name={isExpanded ? "expand_less" : "chevron_right"} size={18} color="var(--mnf-tg)" />
                </button>

                {/* Slot detail dropdown */}
                {isExpanded && (
                  <div
                    className="px-4 pb-4 space-y-3"
                    style={{ borderTop: "1px solid rgba(30,74,53,0.06)", background: "rgba(30,74,53,0.03)" }}
                  >
                    <div className="flex items-center gap-2 pt-3 flex-wrap">
                      <p className="mnf-eyebrow" style={{ width: "100%", marginBottom: 2 }}>
                        Mark this meal as:
                      </p>
                      {(["eaten", "partial", "skipped", "upcoming", "planned"] as SlotStatus[]).map((st) => {
                        const sv2 = statusVisuals(st);
                        const active = status === st;
                        return (
                          <button
                            key={st}
                            onClick={() => markSlot(i, st)}
                            className="mnf-body"
                            style={{
                              height: 32,
                              padding: "0 12px",
                              borderRadius: 999,
                              background: active ? sv2.color : "transparent",
                              color: active ? "#F5F0E8" : sv2.color,
                              border: `1px solid ${sv2.color}`,
                              fontSize: 11.5,
                              fontWeight: 600,
                              cursor: "pointer",
                              textTransform: "capitalize",
                            }}
                          >
                            {sv2.label}
                          </button>
                        );
                      })}
                    </div>

                    <div
                      className="flex items-center gap-3 p-3"
                      style={{
                        background: "rgba(255,255,255,0.6)",
                        borderRadius: 12,
                        border: "1px solid rgba(30,74,53,0.06)",
                      }}
                    >
                      <div className="flex-1">
                        <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>Planned</p>
                        <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>
                          {s.plannedKcal} kcal
                        </p>
                      </div>
                      {!isFutureSlot && (
                        <div className="flex-1">
                          <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>Eaten</p>
                          <p className="mnf-serif" style={{ fontSize: 15, fontWeight: 500, color: sv.color }}>
                            {s.eatenKcal} kcal
                          </p>
                        </div>
                      )}
                      {s.family && (
                        <PillTag tone="coral">
                          <Icon name="diversity_1" size={11} /> Family
                        </PillTag>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {/* Shortfall callout */}
        {hasShortfall && (
          <div
            className="px-4 py-3.5"
            style={{
              background: "linear-gradient(135deg, rgba(192,120,74,0.10) 0%, rgba(245,240,232,0.55) 100%)",
              borderTop: "1px solid rgba(30,74,53,0.06)",
            }}
            data-testid={`shortfall-${day}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <Icon name="insights" size={14} color="#C0784A" />
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>
                {isToday ? "Still to nourish" : "Where you fell short"}
              </p>
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {macroLines.map((m) => {
                const gap = plan.shortfall[m.key];
                const filled = gap === 0;
                return (
                  <div
                    key={m.key}
                    className="px-2 py-2"
                    style={{
                      background: filled ? "rgba(168,212,184,0.20)" : "rgba(255,255,255,0.65)",
                      borderRadius: 12,
                      border: filled ? "1px solid rgba(30,74,53,0.10)" : "1px solid rgba(192,120,74,0.18)",
                      textAlign: "center",
                    }}
                  >
                    <Icon name={filled ? "check" : m.icon} size={12} color={filled ? "#1E4A35" : m.color} />
                    <p className="mnf-serif mt-0.5" style={{ fontSize: 14, fontWeight: 600, color: filled ? "var(--mnf-f)" : m.color, lineHeight: 1 }}>
                      {filled ? "✓" : `-${gap}g`}
                    </p>
                    <p className="text-[9px] mnf-body uppercase" style={{ color: "var(--mnf-tg)", letterSpacing: "0.1em", marginTop: 2 }}>
                      {m.label}
                    </p>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] mnf-body italic mt-2.5" style={{ color: "var(--mnf-tg)" }}>
              {isToday
                ? "A protein-rich evening meal will close the gap, sister."
                : "Tomorrow we plant new seeds."}
            </p>
          </div>
        )}

        {!hasShortfall && isPast && (
          <div
            className="px-4 py-3 flex items-center gap-2"
            style={{
              background: "rgba(168,212,184,0.16)",
              borderTop: "1px solid rgba(30,74,53,0.06)",
            }}
          >
            <Icon name="check_circle" size={14} color="#1E4A35" />
            <p className="text-[12px] mnf-body italic" style={{ color: "var(--mnf-fd)" }}>
              {plan.note}
            </p>
          </div>
        )}

        {/* Bottom actions */}
        <div className="px-4 py-3 flex gap-2" style={{ borderTop: "1px solid rgba(30,74,53,0.06)" }}>
          <Btn
            variant="ghost"
            size="sm"
            block
            onClick={() => setShoppingOpen(!shoppingOpen)}
            data-testid="button-shopping-list"
          >
            <Icon name="shopping_basket" size={14} />
            {shoppingOpen ? "Hide list" : "Shopping list"}
          </Btn>
          <Btn
            variant="primary"
            size="sm"
            block
            onClick={() => setAddSlotOpen(!addSlotOpen)}
            data-testid="button-add-slot"
          >
            <Icon name={addSlotOpen ? "close" : "add"} size={14} />
            {addSlotOpen ? "Cancel" : "Add slot"}
          </Btn>
        </div>

        {/* Shopping list panel — pulls aggregated ingredients from this
            week's planned recipes plus user-added custom items. Persists
            to Convex per (userId, weekStart). */}
        {shoppingOpen && (
          <div
            className="px-4 pb-4 space-y-3"
            style={{ borderTop: "1px solid rgba(30,74,53,0.06)", background: "rgba(245,240,232,0.5)" }}
          >
            <div className="flex items-center justify-between mt-3">
              <p className="mnf-eyebrow">This week's shopping</p>
              <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)", fontSize: 10 }}>
                Week of {weekDates[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </span>
            </div>

            <div className="flex gap-2">
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => { void handlePullPlannedMeals(); }}
                data-testid="button-pull-planned-meals"
                disabled={pulling || !me?.id}
              >
                <Icon name={pulling ? "hourglass_empty" : "auto_awesome"} size={14} />
                {pulling ? "Pulling…" : "Pull from this week's plan"}
              </Btn>
              <Btn
                variant="ghost"
                size="sm"
                onClick={() => {
                    if (!me?.id) return;
                    void clearCheckedGrocery({ userId: me.id, weekStart }).catch((err: unknown) => {
                      reportWriteFailure(
                        "grocery", "clearing checked items", err,
                        "We couldn't clear those",
                        "The checked items are still on your list. Check your connection and try again.",
                      );
                    });
                  }}
                data-testid="button-clear-checked"
                disabled={!groceryItems?.some((i) => i.checked)}
              >
                <Icon name="clear_all" size={14} />
                Clear checked
              </Btn>
            </div>

            {pullStatus && (
              <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
                {pullStatus}
              </p>
            )}

            {/* Items list. Loading=undefined, empty=[]. */}
            {groceryItems === undefined ? (
              <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
                Loading…
              </p>
            ) : groceryItems.length === 0 ? (
              <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)" }}>
                Empty list. Pull from your plan above, or add items below.
              </p>
            ) : (
              <ul className="space-y-1">
                {groceryItems.map((item) => (
                  <li
                    key={item._id}
                    className="flex items-center gap-2 py-1"
                    data-testid={`grocery-item-${item._id}`}
                  >
                    <button
                      onClick={() => {
                          void toggleGroceryChecked({ itemId: item._id }).catch((err: unknown) => {
                            reportWriteFailure(
                              "grocery", "checking an item", err,
                              "That didn't save",
                              "The item didn't change. Check your connection and tap it again.",
                            );
                          });
                        }}
                      aria-label={item.checked ? "Mark as unchecked" : "Mark as checked"}
                      className="flex items-center justify-center"
                      style={{ width: 22, height: 22 }}
                    >
                      <Icon
                        name={item.checked ? "check_circle" : "radio_button_unchecked"}
                        size={18}
                        color={item.checked ? "var(--mnf-pp, #2C6248)" : "var(--mnf-tg)"}
                      />
                    </button>
                    <span
                      className="mnf-body flex-1"
                      style={{
                        fontSize: 13,
                        color: item.checked ? "var(--mnf-tg)" : "var(--mnf-tx)",
                        textDecoration: item.checked ? "line-through" : "none",
                      }}
                    >
                      {typeof item.qty === "number" && item.qty > 0 ? (
                        <span style={{ fontWeight: 500 }}>
                          {Number(item.qty.toFixed(2))} {item.unit ?? ""}{" "}
                        </span>
                      ) : null}
                      {item.displayName}
                    </span>
                    {item.custom && (
                      <span
                        className="mnf-eyebrow"
                        style={{ fontSize: 9, color: "var(--mnf-tg)" }}
                      >
                        added
                      </span>
                    )}
                    <button
                      onClick={() => {
                          void deleteGroceryItem({ itemId: item._id }).catch((err: unknown) => {
                            reportWriteFailure(
                              "grocery", "removing an item", err,
                              "We couldn't remove that",
                              "It's still on your list. Check your connection and try again.",
                            );
                          });
                        }}
                      aria-label="Remove item"
                      className="flex items-center justify-center"
                      style={{ width: 22, height: 22, opacity: 0.6 }}
                    >
                      <Icon name="close" size={14} color="var(--mnf-tg)" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Add custom item */}
            <div
              className="space-y-1.5 pt-2"
              style={{ borderTop: "1px dashed rgba(30,74,53,0.15)" }}
            >
              <p className="mnf-eyebrow mt-2">Add an item</p>
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleAddCustom();
                  }}
                  placeholder="e.g. Olive oil"
                  aria-label="Item name"
                  className="mnf-body flex-1"
                  style={inputStyle}
                />
                <input
                  type="text"
                  value={customQty}
                  onChange={(e) => setCustomQty(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleAddCustom();
                  }}
                  placeholder="Qty"
                  aria-label="Quantity"
                  inputMode="decimal"
                  className="mnf-body"
                  style={{ ...inputStyle, width: 56 }}
                />
                <input
                  type="text"
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleAddCustom();
                  }}
                  placeholder="Unit"
                  aria-label="Unit"
                  className="mnf-body"
                  style={{ ...inputStyle, width: 64 }}
                />
                <Btn
                  variant="primary"
                  size="sm"
                  onClick={() => { void handleAddCustom(); }}
                  data-testid="button-add-grocery-item"
                  disabled={!customName.trim() || !me?.id}
                >
                  <Icon name="add" size={14} />
                </Btn>
              </div>
            </div>
          </div>
        )}

        {/* Add slot panel */}
        {addSlotOpen && (
          <div
            className="px-4 pb-4 space-y-2.5"
            style={{ borderTop: "1px solid rgba(30,74,53,0.06)", background: "rgba(245,240,232,0.5)" }}
          >
            <p className="mnf-eyebrow mt-3">New meal slot</p>
            <div>
              <p className="mnf-eyebrow mb-1">Name</p>
              <input
                type="text"
                value={newSlotName}
                onChange={(e) => setNewSlotName(e.target.value)}
                className="mnf-body"
                style={inputStyle}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="mnf-eyebrow mb-1">Time</p>
                <input
                  type="text"
                  value={newSlotTime}
                  onChange={(e) => setNewSlotTime(e.target.value)}
                  className="mnf-body"
                  style={inputStyle}
                />
              </div>
              <div>
                <p className="mnf-eyebrow mb-1">Target kcal</p>
                <input
                  type="number"
                  value={newSlotKcal}
                  onChange={(e) => setNewSlotKcal(e.target.value)}
                  className="mnf-body"
                  style={inputStyle}
                  min={0}
                />
              </div>
            </div>
            <Btn
              variant="primary"
              block
              size="sm"
              onClick={() => {
                setAddSlotOpen(false);
                setNewSlotName("Afternoon snack");
                setNewSlotTime("2:30 pm");
                setNewSlotKcal("200");
              }}
            >
              <Icon name="add" size={14} /> Add to plan
            </Btn>
          </div>
        )}
      </GlassCard>

      <div className="h-2" />
    </>
  );
}
